import { Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MainLayout } from '@/components/layout/MainLayout';
import { LocaleLayout } from '@/components/layout/LocaleLayout';
import { ProfilePage } from '@/pages/Profile';
import { defaultLng } from '@/lib/i18n/config';

// Page components
import { HomePage } from '@/pages/Home';
import { BlogPage } from '@/pages/Blog';
import { ArticlePage } from '@/pages/Article';
import { CheckoutPage } from '@/pages/Checkout';
import { FormationsPage } from '@/pages/Formations';
import { FormationDetailPage } from '@/pages/FormationDetail';
import { PrestationsPage } from '@/pages/Prestations';
import { DataSciencePage } from '@/pages/prestations/DataScience';
import { AuditExpertisePage } from '@/pages/prestations/AuditExpertise';
import { InterventionsPage } from '@/pages/prestations/Interventions';
import { DossiersTechniquesPage } from '@/pages/prestations/DossiersTechniques';
import { InfographiesPage } from '@/pages/prestations/Infographies';
import { InfographieDetailPage } from '@/pages/infographies/InfographieDetail';
import { DecideurPage } from '@/pages/Decideur';
import { ChercheursPage } from '@/pages/Chercheurs';
import { AgricultureursPage } from '@/pages/Agriculteurs';
import { TechniciensPage } from '@/pages/Techniciens';
import { ContactPage } from '@/pages/Contact';
import { MentionsLegalesPage } from '@/pages/MentionsLegales';
import { CGVPage } from '@/pages/CGV';
import { ConfidentialitePage } from '@/pages/Confidentialite';
import { NotFoundPage } from '@/pages/NotFound';
import { LoginPage } from '@/pages/Login';
import { AuthCallbackPage } from '@/pages/AuthCallback';
import { AboutNewsletterPage } from '@/pages/AboutNewsletter';
import { AboutPage } from '@/pages/About';
import { GlossairePage } from '@/pages/Glossaire';

// Admin components
import { ProtectedAdminRoute } from '@/components/admin/ProtectedAdminRoute';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { AdminLogin } from '@/pages/admin/AdminLogin';
import { AnalyticsDashboard } from '@/pages/admin/AnalyticsDashboard';
import { ArticlesManagement } from '@/pages/admin/ArticlesManagement';
import { UsersManagement } from '@/pages/admin/UsersManagement';
import { FormationsManagement } from '@/pages/admin/FormationsManagement';
import { SettingsPage } from '@/pages/admin/SettingsPage';
import { ArticleEdit } from '@/pages/admin/ArticleEdit';

// Composant helper pour rediriger vers la langue courante
function LocaleRedirect({ to }: { to: string }) {
  const { i18n, ready } = useTranslation();

  // Attendre que i18n soit prêt avant de rediriger
  if (!ready) return null;

  const currentLang = i18n.language?.substring(0, 2) || defaultLng;
  return <Navigate to={`/${currentLang}${to}`} replace />;
}

function App() {
  return (
    <MainLayout>
      <Routes>
        {/* Redirect root to current or default language */}
        <Route path="/" element={<LocaleRedirect to="" />} />

        {/* Redirect old URLs without locale to current language */}
        <Route path="/blog" element={<LocaleRedirect to="/blog" />} />
        <Route path="/blog/*" element={<LocaleRedirect to="/blog" />} />
        <Route path="/formations" element={<LocaleRedirect to="/formations" />} />
        <Route path="/formations/*" element={<LocaleRedirect to="/formations" />} />
        <Route path="/prestations" element={<LocaleRedirect to="/prestations" />} />
        <Route path="/prestations/*" element={<LocaleRedirect to="/prestations" />} />
        <Route path="/contact" element={<LocaleRedirect to="/contact" />} />
        <Route path="/profile" element={<LocaleRedirect to="/profile" />} />

        {/* Localized routes */}
        <Route path="/:locale" element={<LocaleLayout />}>
          <Route index element={<HomePage />} />
          <Route path="blog" element={<BlogPage />} />
          <Route path="blog/:slug" element={<ArticlePage />} />
          <Route path="checkout/article/:slug" element={<CheckoutPage />} />
          <Route path="formations" element={<FormationsPage />} />
          <Route path="formations/:slug" element={<FormationDetailPage />} />
          <Route path="prestations" element={<PrestationsPage />} />
          <Route path="prestations/data-science" element={<DataSciencePage />} />
          <Route path="prestations/dossiers-techniques" element={<DossiersTechniquesPage />} />
          <Route path="prestations/infographies" element={<InfographiesPage />} />
          <Route path="infographies/:slug" element={<InfographieDetailPage />} />
          <Route path="prestations/audit-expertise" element={<AuditExpertisePage />} />
          <Route path="prestations/interventions" element={<InterventionsPage />} />
          <Route path="decideur" element={<DecideurPage />} />
          <Route path="chercheurs" element={<ChercheursPage />} />
          <Route path="agriculteurs" element={<AgricultureursPage />} />
          <Route path="techniciens" element={<TechniciensPage />} />
          <Route path="contact" element={<ContactPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="auth/login" element={<LoginPage />} />
          <Route path="auth/callback" element={<AuthCallbackPage />} />
          <Route path="auth/admin-login" element={<AdminLogin />} />

          {/* Admin routes */}
          <Route
            path="admin"
            element={
              <ProtectedAdminRoute>
                <AdminLayout />
              </ProtectedAdminRoute>
            }
          >
            <Route index element={<Navigate to="analytics" replace />} />
            <Route path="analytics" element={<AnalyticsDashboard />} />
            <Route path="articles" element={<ArticlesManagement />} />
            <Route path="articles/:id/edit" element={<ArticleEdit />} />
            <Route path="users" element={<UsersManagement />} />
            <Route path="formations" element={<FormationsManagement />} />
            <Route path="settings" element={<SettingsPage />} />
          </Route>

          <Route path="about-newsletter" element={<AboutNewsletterPage />} />
          <Route path="about" element={<AboutPage />} />
          <Route path="glossaire" element={<GlossairePage />} />
          <Route path="mentions-legales" element={<MentionsLegalesPage />} />
          <Route path="cgv" element={<CGVPage />} />
          <Route path="confidentialite" element={<ConfidentialitePage />} />
        </Route>

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </MainLayout>
  );
}

export default App;
